import {
    Track,
    Shipment,
    Billing,
    Category,
} from "@/types";
import React, { useState } from "react";
import { useForm, Controller, type SubmitHandler } from "react-hook-form";
import { PiXLight } from "react-icons/pi";
import {
    ActionIcon,
    Button,
    Modal,
    Input,
    Text,
    Select,
    Textarea,
} from "rizzui";
import { PurchaseOrder } from "@/types";

interface AddTrackModalProps {
    show: boolean;
    handleSave: (track: Track) => void;
    title: string;
    description?: string;
}

const AddTrackModal: React.FC<AddTrackModalProps> = ({
    title,
    description,
    handleSave,
}) => {
    const [modalState, setModalState] = useState(false);

    const { control, handleSubmit, reset } = useForm<Track>({
        defaultValues: {
            id: "",
            location: "",
            orderId: "",
            trackId: "",
            remarks: "",
        },
    });

    const onSubmit: SubmitHandler<Track> = (data) => {
        handleSave(data);
        reset();
        setModalState(false);
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        void handleSubmit(onSubmit)(event);
    };

    return (
        <>
            <Button type="button" onClick={() => setModalState(true)}>
                Add Tracking
            </Button>
            <Modal isOpen={modalState} onClose={() => setModalState(false)}>
                <div className="m-auto px-7 pt-6 pb-8">
                    <div className="mb-7 flex items-center justify-between">
                        <div className="flex flex-col ">
                            <Text className="text-lg font-semibold h3">
                                {title}
                            </Text>
                            <p className="text-sm text-gray-500">
                                {description}
                            </p>
                        </div>
                        <ActionIcon
                            size="sm"
                            variant="text"
                            onClick={() => setModalState(false)}
                        >
                            <PiXLight
                                className="h-auto w-6"
                                strokeWidth={1.8}
                            />
                        </ActionIcon>
                    </div>
                    <form onSubmit={handleFormSubmit} noValidate>
                        <div className="mb-4 mt-2">
                            <Controller
                                name="location"
                                control={control}
                                render={({ field }) => (
                                    <Input label="Location" {...field} />
                                )}
                            />
                        </div>

                        <div className="mb-4 mt-2">
                            <Controller
                                name="remarks"
                                control={control}
                                render={({ field }) => (
                                    <Textarea
                                        label="Remarks"
                                        rows={3}
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                        <div className="flex justify-end gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                color="secondary"
                                onClick={() => setModalState(false)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit">Add Tracking</Button>
                        </div>
                    </form>
                </div>
            </Modal>
        </>
    );
};

interface AddShipmentModalProps {
    show: boolean;
    handleSave: (shipment: Shipment) => void;
    title: string;
    description?: string;
}

const AddShipmentModal: React.FC<AddShipmentModalProps> = ({
    title,
    description,
    show,
    handleSave,
}) => {
    const [modalState, setModalState] = useState(false);

    const { control, handleSubmit, reset } = useForm<Shipment>({
        defaultValues: {
            trackingNumber: "",
            shipmentProvider: "",
            estimatedDeliveryDate: "",
            shippingCost: 0,
        },
    });

    const onSubmit: SubmitHandler<Shipment> = (data) => {
        handleSave(data);
        reset();
        setModalState(false);
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        void handleSubmit(onSubmit)(event);
    };

    return (
        <>
            <Button
                type="button"
                onClick={() => setModalState(true)}
                disabled={show}
            >
                Add Shipment
            </Button>
            <Modal isOpen={modalState} onClose={() => setModalState(false)}>
                <div className="m-auto px-7 pt-6 pb-8">
                    <div className="mb-7 flex items-center justify-between">
                        <div className="flex flex-col ">
                            <Text className="text-lg font-semibold h3">
                                {title}
                            </Text>
                            <p className="text-sm text-gray-500">
                                {description}
                            </p>
                        </div>
                        <ActionIcon
                            size="sm"
                            variant="text"
                            onClick={() => setModalState(false)}
                        >
                            <PiXLight
                                className="h-auto w-6"
                                strokeWidth={1.8}
                            />
                        </ActionIcon>
                    </div>
                    <form onSubmit={handleFormSubmit} noValidate>
                        <div className="mb-4 mt-2">
                            <Controller
                                name="trackingNumber"
                                control={control}
                                render={({ field }) => (
                                    <Input label="Tracking Number" {...field} />
                                )}
                            />
                        </div>
                        <div className="mb-4 mt-2">
                            <Controller
                                name="shipmentProvider"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        label="Shipment Provider"
                                        {...field}
                                    />
                                )}
                            />
                        </div>

                        <div className="mb-4 mt-2">
                            <Controller
                                name="estimatedDeliveryDate"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        label="Estimated Delivery Date"
                                        type="date"
                                        {...field}
                                    />
                                )}
                            />
                        </div>

                        <div className="mb-4 mt-2">
                            <Controller
                                name="shippingCost"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        label="Shipping Cost"
                                        type="number"
                                        {...field}
                                    />
                                )}
                            />
                        </div>

                        <div className="flex justify-end gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                color="secondary"
                                onClick={() => setModalState(false)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit">Add Shipment</Button>
                        </div>
                    </form>
                </div>
            </Modal>
        </>
    );
};

interface AddBillingModalProps {
    hide: boolean;
    handleSave: (Billing: Billing) => void;
    title: string;
    color: "primary" | "secondary" | "danger" | undefined;
    buttonType:  "text" | "solid" | "flat" | "outline" | undefined;
    description?: string;
}

const AddBillingModal: React.FC<AddBillingModalProps> = ({
    title,
    description,
    hide: show,
    color,
    buttonType,
    handleSave,
}) => {
    const [modalState, setModalState] = useState(false);

    const { control, handleSubmit, reset } = useForm<Billing>({
        defaultValues: {
            id: "",
            BillingId: "",
            debit: 0,
            credit: 0,
            billingType: "",
            createdDate: "",
            createdUser: "",
            orderId: "",
            client: "",
        },
    });

    const onSubmit: SubmitHandler<Billing> = (data) => {
        handleSave({ ...data, billingType: (data.billingType as any)?.value });
        reset();
        setModalState(false);
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        void handleSubmit(onSubmit)(event);
    };

    return (
        <>
            <Button
                type="button"
                onClick={() => setModalState(true)}
                variant={buttonType}
                color={color}
                disabled={show}
            >
                {title}
            </Button>
            <Modal isOpen={modalState} onClose={() => setModalState(false)}>
                <div className="m-auto px-7 pt-6 pb-8">
                    <div className="mb-7 flex items-center justify-between">
                        <div className="flex flex-col ">
                            <Text className="text-lg font-semibold h3">
                                {title}
                            </Text>
                            <p className="text-sm text-gray-500">
                                {description}
                            </p>
                        </div>
                        <ActionIcon
                            size="sm"
                            variant="text"
                            onClick={() => setModalState(false)}
                        >
                            <PiXLight
                                className="h-auto w-6"
                                strokeWidth={1.8}
                            />
                        </ActionIcon>
                    </div>
                    <form onSubmit={handleFormSubmit} noValidate>
                        <div className="mb-4 mt-2">
                            <Controller
                                name="debit"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        label="Amount"
                                        type="number"
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                        <div className="mb-4 mt-2">
                            <Controller
                                name="billingType"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Billing Type"
                                        options={[
                                            {
                                                value: "Purchase",
                                                label: "Purchase",
                                            },
                                            {
                                                value: "Shipping",
                                                label: "Shipping",
                                            },
                                        ]}
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                        <div className="mb-4 mt-2">
                            <Controller
                                name="remarks"
                                control={control}
                                render={({ field }) => (
                                    <Textarea
                                        label="Remarks"
                                        rows={3}
                                        {...field}
                                    />
                                )}
                            />
                        </div>

                        <div className="flex justify-end gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                color="secondary"
                                onClick={() => setModalState(false)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit">Add Billing</Button>
                        </div>
                    </form>
                </div>
            </Modal>
        </>
    );
};

interface AddPurchaseOrderModalProps {
    show: boolean;
    handleSave: (purchaseOrder: PurchaseOrder) => void;
    title: string;
    description?: string;
    categories: Category[];
}

const AddPurchaseOrderModal: React.FC<AddPurchaseOrderModalProps> = ({
    title,
    description,
    show,
    handleSave,
    categories,
}) => {
    const [modalState, setModalState] = useState(false);

    const { control, handleSubmit, reset } = useForm<PurchaseOrder>({
        defaultValues: {
            id: "",
            quantity: 0,
            price: 0,
            categoryId: "",
            supplier: "",
            weight: 0,
            cubicMeter: 0,
        },
    });

    const onSubmit: SubmitHandler<PurchaseOrder> = (data) => {
        handleSave(data);
        reset();
        setModalState(false);
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        void handleSubmit(onSubmit)(event);
    };

    return (
        <>
            <Button
                type="button"
                onClick={() => setModalState(true)}
                disabled={show}
            >
                Add Purchase Order
            </Button>
            <Modal isOpen={modalState} onClose={() => setModalState(false)}>
                <div className="m-auto px-7 pt-6 pb-8">
                    <div className="mb-7 flex items-center justify-between">
                        <div className="flex flex-col ">
                            <Text className="text-lg font-semibold h3">
                                {title}
                            </Text>
                            <p className="text-sm text-gray-500">
                                {description}
                            </p>
                        </div>
                        <ActionIcon
                            size="sm"
                            variant="text"
                            onClick={() => setModalState(false)}
                        >
                            <PiXLight
                                className="h-auto w-6"
                                strokeWidth={1.8}
                            />
                        </ActionIcon>
                    </div>
                    <form onSubmit={handleFormSubmit} noValidate>
                        <div className="flex flex-wrap">
                            <div className="w-1/2 mb-4 mt-2 pe-3">
                                <Controller
                                    name="quantity"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label="Quantity"
                                            type="number"
                                            inputMode="numeric"
                                            {...field}
                                        />
                                    )}
                                />
                            </div>

                            <div className="w-1/2 mb-4 mt-2">
                                <Controller
                                    name="price"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label="Price"
                                            type="number"
                                            {...field}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="mb-4 mt-2">
                            <Controller
                                name="categoryId"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Category"
                                        labelClassName="font-medium text-gray-900"
                                        placeholder="Select Category"
                                        options={categories.map((c) => ({
                                            label: c.name,
                                            value: c.id,
                                        }))}
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                        <div className="mb-4 mt-2">
                            <Controller
                                name="supplier"
                                control={control}
                                render={({ field }) => (
                                    <Input label="Supplier" {...field} />
                                )}
                            />
                        </div>
                        <div className="flex flex-wrap">
                            <div className="w-1/2 mb-4 mt-2 pe-3">
                                <Controller
                                    name="weight"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label="Weight"
                                            type="number"
                                            inputMode="numeric"
                                            {...field}
                                        />
                                    )}
                                />
                            </div>
                            <div className="w-1/2 mb-4 mt-2">
                                <Controller
                                    name="cubicMeter"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label="Cubic Meter"
                                            type="number"
                                            inputMode="numeric"
                                            {...field}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-4 mt-5">
                            <Button
                                type="button"
                                variant="outline"
                                color="secondary"
                                onClick={() => setModalState(false)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit">Add Purchase Order</Button>
                        </div>
                    </form>
                </div>
            </Modal>
        </>
    );
};

export {
    AddTrackModal,
    AddShipmentModal,
    AddBillingModal,
    AddPurchaseOrderModal,
};
